import { useParams } from 'react-router-dom';
import {
  Page,
  Card,
  BlockStack,
  Text,
  DescriptionList,
  Tooltip,
  Button,
} from '@shopify/polaris';
import { useResource } from '../../../shared/hooks/useResource';
import { LoadingState } from '../../../shared/components/LoadingState';
import { ErrorBanner } from '../../../shared/components/ErrorBanner';
import { ConsignmentStatusBadge } from './ConsignmentStatusBadge';
import { getConsignment, TrackingEvent } from './api';

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleString();
}

function TrackingTimelineRow({ event }: { event: TrackingEvent }): JSX.Element {
  return (
    <Card>
      <BlockStack gap="100">
        <ConsignmentStatusBadge status={event.status} />
        <Text as="p" variant="bodyMd">
          {event.description ?? 'No description'}
        </Text>
        <Text as="p" variant="bodySm" tone="subdued">
          {formatDate(event.occurredAt)}
        </Text>
      </BlockStack>
    </Card>
  );
}

export function ConsignmentDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const query = useResource(['consignment', id], (api) => getConsignment(api, id), Boolean(id));

  return (
    <Page
      title={query.data?.shopifyOrderName ?? id}
      backAction={{ content: 'Consignments', url: '/admin/carrier-control/consignments' }}
    >
      <BlockStack gap="400">
        {query.isLoading ? <LoadingState message="Loading consignment..." /> : null}
        {query.error ? <ErrorBanner error={query.error} /> : null}

        {query.data ? (
          <>
            <Card>
              <DescriptionList
                items={[
                  { term: 'ID', description: query.data.id },
                  {
                    term: 'Status',
                    description: <ConsignmentStatusBadge status={query.data.status} />,
                  },
                  { term: 'Shop', description: query.data.shop.domain },
                  { term: 'Shopify order', description: query.data.shopifyOrderId },
                  { term: 'TNT consignment', description: query.data.tntConsignmentNumber ?? '—' },
                  {
                    term: 'Label',
                    description: query.data.labelUrl ? (
                      <a href={query.data.labelUrl} target="_blank" rel="noreferrer">
                        Download label
                      </a>
                    ) : (
                      '—'
                    ),
                  },
                  { term: 'Created', description: formatDate(query.data.createdAt) },
                  { term: 'Last polled', description: formatDate(query.data.lastPolledAt) },
                ]}
              />
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Actions</Text>
                <Tooltip content="Coming soon">
                  <Button disabled>Retry consignment creation</Button>
                </Tooltip>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <Text as="h2" variant="headingMd">Tracking events</Text>
                {query.data.trackingEvents.length === 0 ? (
                  <Text as="p" variant="bodyMd" tone="subdued">No tracking events yet.</Text>
                ) : (
                  query.data.trackingEvents.map((evt) => (
                    <TrackingTimelineRow event={evt} key={evt.id} />
                  ))
                )}
              </BlockStack>
            </Card>
          </>
        ) : null}
      </BlockStack>
    </Page>
  );
}
